import React from "react";
import { useModalForm } from "../../hooks/useModalForm";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import TextInput from "../fields/TextInput";
import ModalShell from "../modals/ModalShell";
import Button from "../common/Button";
import { Challenge } from "../../types/challenge";
import Switch from "components/switch";
import { logger } from "../../utils/logger";

interface ChallengeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (challengeData: any) => Promise<void>;
  challenge?: Challenge | null;
  mode: "create" | "edit";
  loading?: boolean;
  error?: string | null;
}

const ChallengeModal: React.FC<ChallengeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  challenge,
  mode,
  loading = false,
  error: responseError,
}) => {
  const currentYear = new Date().getFullYear();

  const {
    values: formData,
    setValue: handleInputChange,
    errors,
    isSubmitting,
    handleSubmit,
  } = useModalForm<{
    name: string;
    // "" while the number input is cleared mid-edit
    conducted_at_year: number | "";
    is_active: boolean;
  }>({
    isOpen,
    initialValues:
      mode === "edit" && challenge
        ? {
            name: challenge.name || "",
            conducted_at_year: challenge.conducted_at_year || currentYear,
            is_active: challenge.is_active ?? true,
          }
        : { name: "", conducted_at_year: currentYear, is_active: true },
    validate: (v) => {
      const next: Record<string, string> = {};

      if (!v.name || v.name.trim() === "") {
        next.name = "Challenge name is required";
      } else if (v.name.length > 255) {
        next.name = "Challenge name must not exceed 255 characters";
      }

      if (!v.conducted_at_year) {
        next.conducted_at_year = "Year is required";
      } else {
        const year = parseInt(v.conducted_at_year.toString());
        if (isNaN(year)) {
          next.conducted_at_year = "Invalid year";
        } else if (year < 2000) {
          next.conducted_at_year = "Year cannot be less than 2000";
        } else if (year > 2100) {
          next.conducted_at_year = "Year must not exceed 2100";
        }
      }

      return next;
    },
    onSubmit: async (v) => {
      try {
        await onSubmit({
          name: v.name.trim(),
          conducted_at_year: parseInt(v.conducted_at_year.toString()),
          is_active: v.is_active,
        });
      } catch (error) {
        logger.error("Error submitting form:", error);
      }
    },
  });

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "create" ? "Create New Challenge" : "Edit Challenge"}
      size="2xl"
      closeDisabled={isSubmitting}
      message={responseError ? { text: responseError, type: "error" } : null}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loadingLabel="Saving..."
            disabled={loading}
          >
            {mode === "create" ? "Create Challenge" : "Update Challenge"}
          </Button>
        </>
      }
    >
      <div className="p-6">
        <div className="space-y-4">
          <div>
            <Label>Challenge Name *</Label>
            <TextInput
              type="text"
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              maxLength={255}
              invalid={!!errors.name}
              placeholder="Enter challenge name"
            />
            <FieldError>{errors.name}</FieldError>
          </div>

          <div>
            <Label>Year *</Label>
            <TextInput
              type="number"
              value={formData.conducted_at_year || ""}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange(
                  "conducted_at_year",
                  value ? parseInt(value) : ""
                );
              }}
              invalid={!!errors.conducted_at_year}
              placeholder={currentYear.toString()}
            />
            <FieldError>{errors.conducted_at_year}</FieldError>
          </div>

          <div>
            <div className="flex items-center gap-5">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange("is_active", e.target.checked)
                }
              />
              <div>
                <label
                  htmlFor="is_active"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Active Status
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formData.is_active
                    ? "Switch this off to make this challenge inactive."
                    : "Switch this on to make this challenge active."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default ChallengeModal;
